import { Typography, Grid, Skeleton } from '@mui/material';
import { find, map, split, trim } from 'lodash';
import React, { useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { Button, Dropdown } from '../../components';
import {
  irregularitiesSlice,
  paymentMethodsSlice,
  useDispatch,
  useSelector,
} from '../../stores';
import { handleDateTime, handleMoney } from '../../utils';
import { CreatePaymentMethodModal } from '../PaymentMethods/modals/CreatePaymentMethodModal';

export const IrregularityDetails = () => {
  const dispatch = useDispatch();
  const { cpf } = useSelector((state) => state.profileReducer);
  const { paymentMethods } = useSelector(
    (state) => state.paymentMethodsReducer,
  );
  const { irregularitiesLoading, irregularityDetails } = useSelector(
    (state) => state.irregularitiesReducer,
  );
  const [paymentMethodText, setPaymentMethodText] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<
    | {
        id: number;
        number: string;
        brand: string;
        bankId: number | null;
        default: 0 | 1;
      }
    | 'PIX'
    | undefined
  >(undefined);
  const [error, setError] = useState<string | undefined>();

  const handleSelectedPaymentMethod = (e: string) => {
    if (e !== 'PIX') {
      const paymentMethod = find(paymentMethods, [
        'number',
        trim(split(e.toString(), ' ')[1]),
      ]);
      setPaymentMethod(paymentMethod);
    } else {
      setPaymentMethod('PIX');
    }
    setPaymentMethodText(e.toString());
  };

  const handleSubmit = () => {
    if (!paymentMethod) {
      setError('Escolha um meio de pagamento');
    } else {
      setError(undefined);

      if (irregularityDetails) {
        if (paymentMethod === 'PIX') {
          if (!cpf) {
            dispatch(
              irregularitiesSlice.actions.toggleEditCPFIrregularitiesIsOpen(),
            );
          } else {
            dispatch(irregularitiesSlice.actions.setPaymentMethod('PIX'));
            dispatch(irregularitiesSlice.actions.toggleRegularizeIsOpen());
          }
        } else {
          dispatch(irregularitiesSlice.actions.setPaymentMethod(paymentMethod));
          dispatch(irregularitiesSlice.actions.toggleRegularizeIsOpen());
        }
      }
    }
  };

  return irregularitiesLoading ? (
    <>
      <Skeleton width='100%' height={30} />
      <Skeleton variant='rectangular' width='100%' height={118} />
      <Skeleton width='60%' />
      <Skeleton width='45%' />
      <Skeleton width='80%' />
      <Skeleton width='75%' />
      <Skeleton width='60%' />
    </>
  ) : (
    <>
      <Grid
        container
        rowSpacing={1}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          marginTop: {
            xs: '20px',
            sm: 0,
          },
        }}
      >
        <Grid
          item
          xs={12}
          marginBottom={{
            sm: 3,
          }}
          sx={{ textAlign: 'center' }}
        >
          <Typography
            color='primary'
            sx={{ fontSize: { xs: '4.5vw', sm: '20px' }, fontWeight: 'bold' }}
          >
            Detalhes da irregularidade {irregularityDetails?.irregularity.id}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={5} sx={{ padding: 1 }}>
          {(() => {
            const carouselItems = [];

            // Always add location image (either real image or placeholder)
            carouselItems.push(
              <div key='location' style={{ padding: '10px' }}>
                {irregularityDetails?.irregularity.locationImg ? (
                  <img
                    alt='local'
                    src={irregularityDetails.irregularity.locationImg}
                    style={{ borderRadius: '10px' }}
                  />
                ) : (
                  <div
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      width: '100%',
                      height: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '10px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        textAlign: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      Imagem do local não disponível
                    </Typography>
                  </div>
                )}
              </div>,
            );

            // Always add plate image (either real image or placeholder)
            carouselItems.push(
              <div key='plate' style={{ padding: '10px' }}>
                {irregularityDetails?.irregularity.plateImg ? (
                  <img
                    alt='placa'
                    src={irregularityDetails.irregularity.plateImg}
                    style={{ borderRadius: '10px' }}
                  />
                ) : (
                  <div
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      width: '100%',
                      height: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '10px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        textAlign: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      Imagem da placa não disponível
                    </Typography>
                  </div>
                )}
              </div>,
            );

            // Always add ticket image (either real image or placeholder)
            carouselItems.push(
              <div key='ticket' style={{ padding: '10px' }}>
                {irregularityDetails?.irregularity.ticketImg ? (
                  <img
                    alt='multa'
                    src={irregularityDetails.irregularity.ticketImg}
                    style={{ borderRadius: '10px' }}
                  />
                ) : (
                  <div
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      width: '100%',
                      height: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '10px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        textAlign: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      Imagem da multa não disponível
                    </Typography>
                  </div>
                )}
              </div>,
            );

            return (
              <Carousel
                // statusFormatter={(current, total) => {
                //   return `${current} de ${total}`;
                // }}
                showStatus={false}
                showThumbs={false}
              >
                {carouselItems}
              </Carousel>
            );
          })()}
        </Grid>
        <Grid item xs={12} sm={6} md={7}>
          <Grid xs={12} margin={1}>
            <Typography
              color='primary'
              sx={{ fontSize: { xs: '14px', sm: '16px' } }}
            >
              <b>Placa:</b> {irregularityDetails?.irregularity.plate}
            </Typography>
          </Grid>
          <Grid xs={12} margin={1}>
            <Typography
              color='primary'
              sx={{ fontSize: { xs: '14px', sm: '16px' } }}
            >
              <b>Entrada:</b>{' '}
              {handleDateTime(irregularityDetails?.payment.entryDateTime)}
            </Typography>
          </Grid>
          <Grid xs={12} margin={1}>
            <Typography
              color='primary'
              sx={{ fontSize: { xs: '14px', sm: '16px' } }}
            >
              <b>Saída:</b>{' '}
              {handleDateTime(irregularityDetails?.payment.exitDateTime)}
            </Typography>
          </Grid>
          <Grid xs={12} margin={1}>
            <Typography
              color='primary'
              sx={{ fontSize: { xs: '14px', sm: '16px' } }}
            >
              <b>Período:</b> {irregularityDetails?.payment.stayTime}
            </Typography>
          </Grid>
          <Grid xs={12} margin={1}>
            <Typography
              color='primary'
              sx={{ fontSize: { xs: '14px', sm: '16px' } }}
            >
              <b>Valor:</b> {handleMoney(irregularityDetails?.payment.price)}
            </Typography>
          </Grid>
          {irregularityDetails?.irregularity.address && (
            <Grid xs={12} margin={1}>
              <Typography
                color='primary'
                sx={{ fontSize: { xs: '14px', sm: '16px' } }}
              >
                <b>Endereço:</b> {irregularityDetails?.irregularity.address}
              </Typography>
            </Grid>
          )}
          <Grid
            xs={12}
            rowSpacing={3}
            margin={1}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Dropdown
              options={[
                'PIX',
                ...map(paymentMethods, (item) => {
                  return `**** ${item.number} (${item.brand})`;
                }),
              ]}
              data-testid='paymentMethod'
              margin='normal'
              label='Meio de pagamento'
              type='text'
              value={paymentMethodText}
              onChange={handleSelectedPaymentMethod}
              errorMessage={error}
            />
          </Grid>
          <Grid
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button
              variant='text'
              label='Cadastrar meio de pagamento'
              type='submit'
              onClick={() => {
                dispatch(
                  paymentMethodsSlice.actions.toggleCreatePaymentMethodIsOpen(),
                );
                // navigate(PAYMENT_METHODS);
              }}
              color='primary'
              style={{ width: '100%', marginTop: 10 }}
            />
            <Button
              variant='contained'
              label='Pagar irregularidade'
              onClick={handleSubmit}
              color='secondary'
              loading={irregularitiesLoading}
              style={{ width: '100%', marginTop: 10 }}
            />
            <Button
              label='Voltar'
              type='submit'
              onClick={() =>
                dispatch(irregularitiesSlice.actions.toggleShowDetails())
              }
              color='primary'
              style={{ width: '100%', marginTop: 10 }}
            />
          </Grid>
        </Grid>
      </Grid>
      <CreatePaymentMethodModal />
    </>
  );
};
